import * as React from "react";

import { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { Cookiebot, CookiePreferences, WritableCookiePreferences } from "../../types";
import { errorProxyWrapper } from "../../util";
import { useImmer } from "use-immer";
import * as ramda from "ramda";
import { datadogRum } from "@datadog/browser-rum";
import posthog from "posthog-js";

const normalizeCookiePreferences = (prefs: Cookiebot["consent"]): CookiePreferences => ({
  marketing: prefs.marketing,
  analytics: prefs.statistics,
  preferences: prefs.preferences,
  functional: true,
});

export type CookieType = keyof WritableCookiePreferences;

const cookieConsentError = new Error(
  "Could not find cooke context, is this component a child of <CookieConsentProvider />?",
);

interface CookieConsentContext {
  updatePrefs: (key: CookieType, value: boolean) => void;
  savePrefs: (overrideUnsaved?: Partial<WritableCookiePreferences>) => void;
  essentialOnly: () => void;
  setShowBanner: (showBanner: boolean) => void;
  setShowModal: (showModal: boolean) => void;
  unsavedPreferences: CookiePreferences;
  savedPreferences: CookiePreferences;
  hasSetConsent: boolean;
  loaded: boolean;
  showBanner: boolean;
  showModal: boolean;
}

const CookieConsentContext = React.createContext<CookieConsentContext>(
  errorProxyWrapper({} as unknown as CookieConsentContext, cookieConsentError),
);

const defaultPrefs: CookiePreferences = {
  marketing: true,
  analytics: true,
  preferences: true,
  functional: true,
};

export const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hasSetConsent, setHasSetConsent] = useState(false);
  const [unsavedPreferences, setUnsavedPreferences] = useImmer<CookiePreferences>(defaultPrefs);
  const [savedPreferences, setSavedPreferences] = useState<CookiePreferences>(defaultPrefs);
  const [loaded, setLoaded] = useState(false);

  // ssr sucks
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    let timesTried = 0;

    const checkLoadedHandler = () => {
      if (timesTried > 10) {
        clearInterval(interval);
      }
      if (!window.Cookiebot) {
        timesTried++;
        return;
      }

      const hasSetConsent = window.Cookiebot.consent.stamp !== "0";

      const { gdprApplies, lgpdApplies, ccpaApplies } = window.Cookiebot.regulations;
      const requiresExplicitConsent = gdprApplies || lgpdApplies || ccpaApplies;

      let shouldShow = !hasSetConsent;

      // if the user has not yet set consent, and they are in a region that does not require explicit consent
      // submit implied consent
      const canSubmitImpliedConsent = !requiresExplicitConsent && !hasSetConsent;
      if (canSubmitImpliedConsent) {
        window.Cookiebot.submitCustomConsent(true, true, true);
        shouldShow = false;
      }

      setUnsavedPreferences(normalizeCookiePreferences(window.Cookiebot.consent));
      setSavedPreferences(normalizeCookiePreferences(window.Cookiebot.consent));
      setHasSetConsent(hasSetConsent);
      setShowBanner(shouldShow);
      setLoaded(true);
      clearInterval(interval);
    };

    interval = setInterval(checkLoadedHandler, 500);

    return () => {
      clearInterval(interval);
    };
  }, [setUnsavedPreferences, setHasSetConsent, setShowBanner, loaded, setLoaded]);

  useEffect(() => {
    const onCookiebotUpdateConsent = () => {
      if (window.Cookiebot.consent.statistics) {
        datadogRum.setTrackingConsent("granted");
        posthog.opt_in_capturing();
        posthog.set_config({ persistence: "localStorage+cookie" });
      }

      setSavedPreferences(normalizeCookiePreferences(window.Cookiebot.consent));
      setUnsavedPreferences(normalizeCookiePreferences(window.Cookiebot.consent));
    };

    window.addEventListener("CookiebotOnConsentReady", onCookiebotUpdateConsent);
    window.addEventListener("CookiebotOnLoad", onCookiebotUpdateConsent);

    return () => {
      window.removeEventListener("CookiebotOnConsentReady", onCookiebotUpdateConsent);
      window.removeEventListener("CookiebotOnLoad", onCookiebotUpdateConsent);
    };
  }, [setUnsavedPreferences, loaded]);

  const updatePrefs = useCallback(
    (key: CookieType, value: boolean) => {
      setUnsavedPreferences((draft) => {
        draft[key] = value;
      });
    },
    [setUnsavedPreferences],
  );

  useEffect(() => {
    if (!window.Cookiebot) {
      return;
    }
    const savedConsent = normalizeCookiePreferences(window.Cookiebot.consent);
  }, [unsavedPreferences]);

  const savePrefs = useCallback(
    (argPrefs?: Partial<WritableCookiePreferences>) => {
      const prefsToUse = { ...unsavedPreferences, ...argPrefs };
      const { marketing, analytics, preferences: cookiePref } = prefsToUse;

      setUnsavedPreferences(prefsToUse);
      setHasSetConsent(true);
      setShowBanner(false);
      window.Cookiebot?.submitCustomConsent(cookiePref, analytics, marketing);
    },
    [setShowBanner, unsavedPreferences, setUnsavedPreferences],
  );

  const essentialOnly = useCallback(() => {
    savePrefs({
      marketing: false,
      analytics: false,
      preferences: false,
    });
  }, [savePrefs]);

  const ctxValue = useMemo(
    () => ({
      savePrefs,
      updatePrefs,
      savedPreferences,
      unsavedPreferences,
      loaded,
      essentialOnly,
      setShowBanner,
      hasSetConsent,
      showBanner,
      showModal,
      setShowModal,
    }),
    [
      updatePrefs,
      loaded,
      savePrefs,
      savedPreferences,
      unsavedPreferences,
      setShowBanner,
      essentialOnly,
      hasSetConsent,
      showBanner,
      showModal,
      setShowModal,
    ],
  );

  return <CookieConsentContext.Provider value={ctxValue}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = () => useContext(CookieConsentContext);
